// @flow
import { values, get, toString, merge, debounce, size, pick } from 'lodash';
import moment from 'moment';
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Agenda as AgendaView } from '@views';
import type { AgendaFilters } from '@types';
import { actions } from '@context/modals';
import { isPresent, Log, formatDateToApi, getPlatform, useDelayedFlag, checkIfIsMobile } from '@helpers/helpers';
import { appointmentActions, appointmentsRequestKey, appointmentsRequestCovers } from '@context/appointments';
import { mapPlanIncludes, mapPermissionTo } from '@helpers/connect';
import { deepEqual } from 'fast-equals';
import AgendaLiveUpdates from './AgendaLiveUpdates';
import AppointmentService from '../services/AppointmentService';

const shallowEqual = (l, r) => deepEqual(l, r);

const getHighPrivacy = (state: Object) => {
  const highPrivacyPatients = get(state, ['context', 'clinics', 'activeClinic', 'highPrivacyPatients'], false);
  const isManager = get(state, ['context', 'user', 'advancedData', 'manager'], false);
  const isDoctor = get(state, ['context', 'user', 'roleType'], '') === 'Doctor';

  return highPrivacyPatients && !isManager && isDoctor;
};



const API_DATE = 'YYYY-MM-DD HH:mm:ss';

// What a fetch asks for: the selected date's day, ISO week or month
const appointmentsRange = (selectedDate: any, singleDay: any) => {
  const date = moment.utc(moment(selectedDate).isValid() ? selectedDate : formatDateToApi(new Date()), API_DATE);
  let unit = 'isoWeek';
  if (singleDay === 'month') unit = 'month';
  else if (singleDay) unit = 'day';
  return { start: date.clone().startOf(unit).format(API_DATE), end: date.clone().endOf(unit).format(API_DATE) };
};

// The days on screen for a switchMode: the Android app's day, week or month; the iPhone's day
// (its Calendar is always timeGridDay); the desktop layout's week or day (iPad landscape too)
const visibleMode = (switchMode: string, isMobileLayout: boolean, isAndroid: boolean) => {
  if (isMobileLayout && isAndroid) return switchMode;
  if (isMobileLayout) return 'day';
  return switchMode === 'week' ? 'week' : 'day';
};

// A range that isn't on screen yet: the top bar only once its request has taken this long (a quick
// answer shows nothing), and then for at least this long (no blink)
const LOADING_BAR_DELAY = 400;
const LOADING_BAR_MIN_TIME = 300;
// "Atualizar" turns its icon at once, for whole turns (Agenda.scss: 800 ms each), until its request answers
const REFRESH_SPIN_TURN = 800;

const Agenda = () => {
  const appointments = useSelector(state => state.context.appointments, shallowEqual);
  const highPrivacy = useSelector(state => getHighPrivacy(state), shallowEqual);
  const agendaFilters = useSelector(state => get(state, ['context', 'appointments', 'filters'], {}), shallowEqual);
  const loadingItems = useSelector(state => get(state, ['context', 'appointments', 'loadingItems'], {}), shallowEqual);
  const user = useSelector(state => state.context.user, shallowEqual);
  const activeClinic = useSelector(state => get(state, ['context', 'clinics', 'activeClinic'], {}), shallowEqual);
  const isSecretary = useSelector(state => get(state, ['context', 'user', 'roleType'], '') !== 'Doctor', shallowEqual);
  const currentDoctorID = useSelector(state => get(state, ['context', 'user', 'advancedData', 'doctorId'], 0), shallowEqual);
  const orderDoctorsByProfession = useSelector(state => get(state, ['context', 'clinics', 'activeClinic', 'orderDoctorsByProfession'], false), shallowEqual);
  const doctors = useSelector(state => state.context.doctors, shallowEqual);
  const modals = useSelector(state => state.context.modals, shallowEqual);
  const planInclude = useSelector(state => mapPlanIncludes(state), shallowEqual);
  const permissions = useSelector(state => mapPermissionTo(state), shallowEqual);

  const [appointmentId, setAppointmentId] = useState(null);
  const [immediateLoading, setImmediateLoading] = useState(false);
  const dispatch = useDispatch();
  const initialized = useRef(false);

  const debouncedSearch = useRef(debounce((start: any, end: any, hardReload: boolean) => {
    if (start) {
      dispatch(appointmentActions.getAppointments(start, end, hardReload));
    } else {
      Log.error(`Invalid date ${start}`);
    }
    // The request has put its own isLoading in the store by now
    setImmediateLoading(false);
  }, 300));

  const requestInFlight = get(appointments, 'isLoading', false);
  // A request is waiting out the debounce or on its way
  const isFetching = requestInFlight || immediateLoading;
  // The days "Atualizar" was asked for, until its request answers
  const [refreshKey, setRefreshKey] = useState(null);


  const getDoctorsFilter = useCallback(() => {
    const doctorsFilter = get(agendaFilters, 'doctorsFilter', null);

    if (doctorsFilter) {
      return doctorsFilter;
    }

    if (isSecretary) {
      return [parseInt(Object.keys(get(doctors, 'data', {}))[0], 10)];
    }

    return [currentDoctorID];
  }, [agendaFilters, currentDoctorID, doctors, isSecretary]);


  const handleGetAppointments = useCallback((singleDay, hardReload: boolean = false, incCalendarDate) => {
    let selectedDate = get(agendaFilters, ['selectedDate'], formatDateToApi(new Date()));

    if (incCalendarDate) {
      selectedDate = moment.utc(incCalendarDate).format(API_DATE);
    }

    const { start, end } = appointmentsRange(selectedDate, singleDay);

    const appointmentDateFilters = get(appointments, ['filters', 'data']);
    if (!appointmentDateFilters) {
      debouncedSearch.current(start, end, hardReload === true);
    } else {
      debouncedSearch.current(start, end, hardReload === true);
    }
    setImmediateLoading(true);
  }, [agendaFilters, appointments]);

  const handleShowAppointment = useCallback((event) => {
    if (get(loadingItems, String(get(event, 'id', '')), false)) return;

    const data = {
      appointmentId: get(event, 'id', ''),
      doctorIds: Object.keys(get(doctors, 'data', {})),
      agendaFilters,
    };

    dispatch(actions.openModal('AppointmentModalForm', data));
    setAppointmentId(get(event, 'id', ''));
  }, [agendaFilters, dispatch, doctors, loadingItems]);

  const handleUpdateAppointment = useCallback((appointment) => {
    dispatch(appointmentActions.updateAppointment(appointment));
  }, [dispatch]);

  const handleNewAppointment = useCallback((doctorsFilter, start, end, isDayClick) => {
    const date = get(agendaFilters, 'selectedDate') ?
      moment(get(agendaFilters, 'selectedDate', moment())).format('YYYY-MM-DD HH:mm:ss')
      : moment(start).format('YYYY-MM-DD HH:mm:ss');

    const data: any = {
      doctorIds: Object.keys(get(doctors, 'data', {})),
      agendaFilters,
      date: moment(date).format('YYYY-MM-DD HH:mm:ss'),
      isDayClick,
    };

    if (moment.isMoment(start)) {
      data.time = moment(start).format('HH:mm');
    } else {
      data.time = moment().hour(9).minute(0).format('HH:mm');
    }

    if (start && end) {
      merge(data, {
        date: moment(start).format('YYYY-MM-DD HH:mm:ss'),
        time: moment(start).format('HH:mm'),
        duration: moment.duration(moment.utc(end).diff(moment.utc(start))).asMinutes(),
        startDate: moment(start).format('YYYY-MM-DD HH:mm:ss'),
        endDate: moment(end).format('YYYY-MM-DD HH:mm:ss'),
      });
    }

    dispatch(actions.openModal('NewAppointmentModal', data));
    setAppointmentId(null);
  }, [agendaFilters, dispatch, doctors]);

  const handleChangeAgendaFilters = useCallback((filters: AgendaFilters) => {
    dispatch(appointmentActions.setAgendaFilters(filters));
  }, [dispatch]);

  const isAndroid = useMemo(() => getPlatform() === 'android', []);
  // The phone layout (see PlatformSwitch), as a boolean so other ui changes don't render the agenda again
  const isMobileLayout = useSelector(state => checkIfIsMobile(state.ui));

  useEffect(() => {
    if (initialized.current === false) {
      const params = window.location.hash.replace('#/agenda?', '').split(/&|=/g);
      const doctorId = params[3];
      const date = params[1];

      const newFilters = {};

      if (isPresent(date)) {
        newFilters.selectedDate = moment(date, 'DD-MM-YYYY').format('YYYY-MM-DD HH:mm:ss');
      }

      if (doctorId && isPresent(doctorId) && doctorId !== 'null' && parseInt(doctorId, 10) > 0) {
        newFilters.doctorsFilter = [parseInt(doctorId, 10)];
      }

      if (isAndroid && !get(agendaFilters, 'switchMode')) {
        newFilters.switchMode = 'week';
      }

      const currentMode = newFilters.switchMode || get(agendaFilters, 'switchMode') || 'week';
      // The days on screen: the iPhone's Calendar is always a day, whatever the persisted switchMode
      const singleDayParam = AppointmentService.singleDayParam(visibleMode(currentMode, isMobileLayout, isAndroid));

      handleChangeAgendaFilters(newFilters);
      handleGetAppointments(singleDayParam, true);
      initialized.current = true;
    }
  },
  // eslint-disable-next-line
  []);


  const activeModal = useMemo(() => get(modals, ['active'], false), [modals]);
  const fullAppointment = useMemo(() => get(appointments, ['data', 'full', toString(appointmentId)]), [appointmentId, appointments]);
  const selectedDate = useMemo(() => get(agendaFilters, 'selectedDate', moment().format('YYYY-MM-DD HH:mm:ss')) || moment().format('YYYY-MM-DD HH:mm:ss'), [agendaFilters]);
  const compactData = useMemo(() => get(appointments, ['data', 'compact'], {}), [appointments]);
  // const startOfSelectionWeek = useMemo(() => moment(selectedDate).startOf('isoWeek').isoWeekday(1).format('YYYY-MM-DD'), [selectedDate]);
  // const appointmentDateFilter = useMemo(() => get(appointments, ['filters', 'data', startOfSelectionWeek]), [appointments, startOfSelectionWeek]);

  const defaultSwitchMode = 'week';
  // Use `||` (not get's default arg) so a persisted/rehydrated `null` also falls
  // back to the weekly default — lodash get only defaults on `undefined`.
  const agendaSwitchMode = get(agendaFilters, 'switchMode') || defaultSwitchMode;

  const activeIncomingKey = useMemo(() => {
    // Month fetches use startOf(month) as API startDate; incomingKeys live under that week
    // bucket, not under the user's selected day (CREATE_SUCCESS merges into the same one).
    // From the selected date, not the store's activeIncomingKey: picking a date points that at the
    // date's week until the month's request goes out, which made a month already loaded flash as loading.
    const date = moment.utc(selectedDate);
    return (agendaSwitchMode === 'month' ? date.startOf('month') : date).startOf('isoWeek').format('YYYY-MM-DD');
  }, [agendaSwitchMode, selectedDate]);

  const compactAppointments = useMemo(() => {
    const incomingKeys = get(appointments, ['incomingKeys', activeIncomingKey], []) || [];
    return pick(compactData, incomingKeys);
  }, [activeIncomingKey, appointments, compactData]);

  // The days on screen, as a request for them would ask
  const visibleRange = useMemo(() => {
    const selected = get(agendaFilters, ['selectedDate'], formatDateToApi(new Date()));
    return appointmentsRange(selected, AppointmentService.singleDayParam(visibleMode(agendaSwitchMode, isMobileLayout, isAndroid)));
  }, [agendaFilters, agendaSwitchMode, isAndroid, isMobileLayout]);
  const visibleKey = appointmentsRequestKey(visibleRange.start, visibleRange.end);

  // Whether the appointments on screen came from a request for these days (this day, week or month,
  // or a range holding it). Until then an empty day may still have appointments on their way.
  const isRangeLoaded = useMemo(() => (
    appointmentsRequestCovers(get(appointments, ['incomingRanges', activeIncomingKey]), visibleRange.start, visibleRange.end)
  ), [activeIncomingKey, appointments, visibleRange]);

  // Loading is only about days not on screen yet. Days already on screen (landing on the agenda again,
  // a week seen before, a live update) refresh out of sight and simply change if something changed.
  const isLoading = isFetching && !isRangeLoaded;

  // The latest request for this range failed: say so instead of "no appointments"
  const rangeFailed = !isRangeLoaded && !isFetching && size(get(appointments, 'errors')) > 0;

  useEffect(() => () => debouncedSearch.current.cancel(), []);

  // "Atualizar" lasts until its request answers, and only for the days it was asked for: moving to
  // other days hands them over to the loading bar
  const refreshing = refreshKey !== null && refreshKey === visibleKey;
  useEffect(() => {
    if (refreshKey !== null && (!isFetching || refreshKey !== visibleKey)) setRefreshKey(null);
  }, [isFetching, refreshKey, visibleKey]);

  // "Atualizar" shows its own progress, so the top bar stays for days that aren't on screen yet
  const showLoadingBar = useDelayedFlag(requestInFlight && !isRangeLoaded && !refreshing, LOADING_BAR_DELAY, LOADING_BAR_MIN_TIME);
  const isRefreshing = useDelayedFlag(refreshing, 0, REFRESH_SPIN_TURN, REFRESH_SPIN_TURN);

  const agendaFiltersRender = useMemo(() => ({
    selectedDate,
    switchMode: get(agendaFilters, 'switchMode') || defaultSwitchMode,
    roomsFilter: get(agendaFilters, 'roomsFilter', null),
    doctorsFilter: getDoctorsFilter(),
  }), [agendaFilters, defaultSwitchMode, getDoctorsFilter, selectedDate]);


  const handleLiveRefresh = useCallback(() => {
    handleGetAppointments(agendaSwitchMode === 'month' ? 'month' : null, true);
  }, [agendaSwitchMode, handleGetAppointments]);

  // Set in the same click as handleGetAppointments' immediate loading, so it ends with that request.
  // A second tap while it's on its way changes nothing.
  const handleRefresh = useCallback((singleDay) => {
    if (refreshing) return;
    setRefreshKey(visibleKey);
    handleGetAppointments(singleDay, true);
  }, [handleGetAppointments, refreshing, visibleKey]);

  return (
    <>
      <AgendaLiveUpdates onRefresh={handleLiveRefresh} />
      <AgendaView
        agendaFilters={agendaFiltersRender}
        activeModal={activeModal}
        appointments={values(compactAppointments)}
        isLoading={isLoading}
        isRangeLoaded={isRangeLoaded}
        rangeFailed={rangeFailed}
        showLoadingBar={showLoadingBar}
        isRefreshing={isRefreshing}
        onRefresh={handleRefresh}
        activeClinic={activeClinic}
        doctors={doctors}
        user={user}
        fullAppointment={fullAppointment}
        onGetAppointments={handleGetAppointments}
        onShowAppointment={handleShowAppointment}
        onUpdateAppointment={handleUpdateAppointment}
        onNewAppointmentClick={handleNewAppointment}
        onChangeAgendaFilters={handleChangeAgendaFilters}
        loadingItems={loadingItems}
        highPrivacy={highPrivacy}
        orderDoctorsByProfession={orderDoctorsByProfession}
        isFinatialEnabled={planInclude.financial}
        permissions={permissions}
      />
    </>
  );
};



export default Agenda;
